import { ColumnsType } from 'antd/es/table'
import { saveAs } from 'file-saver'
import moment from 'moment'

import formatValueCsv, { ExportColumn } from '../exportUtils'
import processRows from '../processRows'

export interface SumDataEntry {
  title?: string
  columns: ExportColumn[]
  dataSource: Record<string, unknown>[]
}

interface ExportToCSVParams<T extends object> {
  columns: ColumnsType<T>
  dataSource: T[]
  reportName: string
  exportDetails?: boolean
  sumData?: SumDataEntry[]
}

type CellValue = string | number

const processSummaryRows = (sumData: SumDataEntry[] = []): CellValue[][] => {
  const rows: CellValue[][] = []

  if (sumData.length === 0) return rows

  sumData.forEach((summary) => {
    rows.push([], [])

    if (summary.title) rows.push([summary.title])

    if (summary.columns.length > 0) rows.push(summary.columns.map((col) => col.title))

    summary.dataSource.forEach((record) => {
      const row = summary.columns.map((col) => formatValueCsv(record[col.dataIndex], col, record))
      rows.push(row)
    })
  })

  return rows
}

const generateCSVContent = (rows: CellValue[][]): string =>
  rows.map((row) => row.map((tuple) => `"${String(tuple)}"`).join(';')).join('\n')

const exportToCSV = <T extends object>({
  columns,
  dataSource,
  reportName,
  exportDetails = false,
  sumData = [],
}: ExportToCSVParams<T>): void => {
  const cols = columns as unknown as ExportColumn[]

  const detailColumnDef = cols.find((col) => col.columns)
  const mainColumns = cols.filter((col) => (!col.columns || col.columns.length === 0) && col.exportable !== false)
  const detailColumns = detailColumnDef?.columns
    ? detailColumnDef.columns.filter((col) => col.exportable !== false)
    : []
  const activeColumns = exportDetails ? [...mainColumns, ...detailColumns] : mainColumns

  const header = activeColumns.map((column) => column.title)
  const body = processRows({
    dataSource: dataSource as unknown as Record<string, unknown>[],
    mainColumns,
    detailColumns,
    detailDataIndex: detailColumnDef?.dataIndex ?? 'detalhes',
    activeColumns,
    exportDetails,
  })
  const summaryRows = processSummaryRows(sumData)

  const finalRows = [header, ...body, ...summaryRows]
  const csvContent = generateCSVContent(finalRows)
  const reportTimeStamp = moment().format('DD_MM_YYYY_HH_mm_ss')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  saveAs(blob, `relatorio_${reportName}_${reportTimeStamp}.csv`)
}

export default exportToCSV
